import { useState, useEffect } from "react";
import Stack from "@mui/material/Stack";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMoreRounded";

import type { TypographyProps } from "@mui/material/Typography";

const TRANSITION_DURATION = 300;

type CollapsibleProps = {
  title: string;
  activeValue?: string;
  secondaryValue?: string;
  open?: boolean;
  defaultOpen?: boolean;
  iconVariant?: "arrow" | "plus" | null;
  marginBottom?: number;
  keepChildrenMounted?: boolean;
  children?: React.ReactNode;
  TitleProps?: TypographyProps;
  ActiveValueProps?: TypographyProps;
  SecondaryTextProps?: TypographyProps;
  handleToggle?: () => void;
};

/**
 * Make sure to pass 'ActiveValueProps' if 'activeValue' or 'secondaryValue' is being passed
 */
export default function Collapsible({
  title,
  activeValue,
  secondaryValue,
  open,
  defaultOpen = false,
  iconVariant = "plus",
  TitleProps,
  ActiveValueProps,
  SecondaryTextProps,
  marginBottom = 2,
  keepChildrenMounted = true,
  children,
  handleToggle,
}: CollapsibleProps) {
  const [isOpen, setIsOpen] = useState<boolean>(typeof open === "undefined" ? defaultOpen : open);
  const shouldExpand = typeof open === "undefined" ? isOpen : open;
  const [shouldRender, setShouldRender] = useState<boolean>(true && keepChildrenMounted);

  useEffect(() => {
    if (keepChildrenMounted) return;

    if (shouldExpand) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), TRANSITION_DURATION); // Adjust timing to match your transition
      return () => clearTimeout(timer);
    }
  }, [shouldExpand, keepChildrenMounted]);

  const toggleCollapse = () => {
    if (typeof handleToggle === "function") {
      return handleToggle();
    }

    setIsOpen((prev) => !prev);
  };

  return (
    <div>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={toggleCollapse}
        mb={marginBottom}
        columnGap={1.5}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexGrow: 1,
            columnGap: "1rem",
            // icon alignment works well with this
            alignItems: "baseline",
          }}
        >
          <span>
            <Typography
              {...TitleProps}
              display="inline-block"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            {secondaryValue && (
              <Typography {...SecondaryTextProps} ml={0.5} display="inline-block">
                {secondaryValue}
              </Typography>
            )}
          </span>
          {activeValue && <Typography {...ActiveValueProps}>{activeValue}</Typography>}
        </div>

        {iconVariant !== null ? (
          iconVariant === "plus" ? (
            <AddIcon
              sx={{ transition: "all 0.3s ease", rotate: `${shouldExpand ? "135deg" : "0deg"}` }}
            />
          ) : (
            <ExpandMoreIcon
              sx={{ transition: "all 0.3s ease", rotate: `${shouldExpand ? "180deg" : "0deg"}` }}
            />
          )
        ) : null}
      </Stack>

      <Collapse in={shouldExpand && shouldRender} timeout={TRANSITION_DURATION}>
        {shouldRender && children}
      </Collapse>
    </div>
  );
}
